import React, { useCallback, useEffect, useMemo, useState } from 'react'
import Swal from 'sweetalert2'
import { Bar, Line } from 'react-chartjs-2'
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js'

import IPInput from '../../../../../components/IPInput/IPInput'
import IPButton from '../../../../../components/IPButton/IPButton'
import IPCheckbox from '../../../../../components/IPCheckBox'
import IPSelectBox from '../../../../../components/IPSelectBox'
import { ISelectOption } from '../../../../../components/IPSelectBox/IPSelectBox'
import InputCampaign from '../../../../../components/InputCampaign'
import Table from '../../../../../components/Table'
import { BaseComponentWithContextProps, componentWithContext } from '../../../../../base/customHooks/componentHOC'
import { exportToXLSX } from '../../../../../base/utils/dataHelper'

import {
  VodafoneGeneralReport,
  VodafoneGeneralReportFilter,
  VodafoneGeneralReportRequest,
} from '../models/model'
import { GetVodafoneGeneralReportApi } from '../services/getVodafoneGeneralReportApi'
import { GetVodafoneGeneralReportFilterApi } from '../services/getVodafoneGeneralReportFilterApi'

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, BarElement, Title, Tooltip, Legend)

/**
 * Tek tarih aralığının hangi tarihe uygulanacağı. Servis, satış VEYA aktivasyon tarihi aralıkta olan satırları
 * döndürür; eksen bu küme üzerinde istemcide uygulandığı için değiştirmek yeni sorgu gerektirmez.
 * 'aktivasyon'  → yalnız aktivasyon tarihi aralıkta olan satırlar; her şey aktivasyon tarihine göre.
 * 'satis'       → yalnız satış (fatura) tarihi aralıkta olan satırlar; her şey satış tarihine göre.
 * 'tum'         → tüm satırlar; satış metrikleri satış tarihi, aktivasyon metrikleri aktivasyon tarihi aralıkta
 *                 olanlardan sayılır ve grafikte kendi tarihine kovalanır (varsayılan).
 */
type ReportAxis = 'aktivasyon' | 'satis' | 'tum'
type ActivationType = '' | 'temlik' | 'kontrat' | 'mikro'
// '' = Tümü (servise gönderilmez), 'evet' = true, 'hayir' = false
type DemoFilter = '' | 'evet' | 'hayir'

interface IDashboardRow {
  salesDate: Date | null
  activationDate: Date | null
  brand: string
  model: string
  urunTipi: string
  urunKodu: string
  malzemeNo: string
  kota: string
  kotaAciklama: string
  urunTuru: string
  hiyerarsi: string
  hiyerarsiAciklama: string
  dealerCode: string
  dealerName: string
  salesRep: string
  channelManager: string
  regionManager: string
  currency: string
  sales: number
  netAmount: number
  temlik: number
  kontrat: number
  mikro: number
  activation: number
  vdfAktivasyonTutari: number
  aktivasyonTutari: number
}

interface IGroupItem {
  key: string
  label: string
  sales: number
  activation: number
  temlik: number
  kontrat: number
  mikro: number
}

const COLORS = {
  blue: '#2563eb',
  green: '#16a34a',
  orange: '#f97316',
}

const REPORT_AXIS_OPTIONS: { value: ReportAxis; label: string; note: string }[] = [
  {
    value: 'aktivasyon',
    label: 'Aktivasyon',
    note: 'Tarih aralığı aktivasyon tarihine uygulanır: yalnız aktivasyonu aralıkta olan satırlar gösterilir, bu cihazların satış tarihi aralık dışında olabilir. Kutucuklar, tablo, sıralamalar, Excel ve grafikler aktivasyon tarihine göre hesaplanır.',
  },
  {
    value: 'satis',
    label: 'Satış',
    note: 'Tarih aralığı satış (fatura) tarihine uygulanır: yalnız satışı aralıkta olan satırlar gösterilir, bu cihazların aktivasyonları tarihinden bağımsız sayılır. Kutucuklar, tablo, sıralamalar, Excel ve grafikler satış tarihine göre hesaplanır.',
  },
  {
    value: 'tum',
    label: 'Tümü',
    note: 'Satış veya aktivasyon tarihi aralıkta olan tüm satırlar gösterilir. Satış rakamları yalnız satışı aralıkta olanlardan, aktivasyon rakamları yalnız aktivasyonu aralıkta olanlardan sayılır; grafiklerde her metrik kendi tarihine kovalanır. Tablo ve Excel satırların tamamını içerir.',
  },
]

const ACTIVATION_TYPE_OPTIONS: ISelectOption[] = [
  { value: 'temlik', label: 'Temlik' },
  { value: 'kontrat', label: 'Peşin Kontrat' },
  { value: 'mikro', label: 'Mikro Kredi' },
]

const DEMO_OPTIONS: ISelectOption[] = [
  { value: 'evet', label: 'Evet' },
  { value: 'hayir', label: 'Hayır' },
]

/** string[] → IPSelectBox seçenekleri (value = label). */
const toSelectOptions = (values: string[]): ISelectOption[] => values.map(value => ({ value, label: value }))

/** Seçili değer listesini IPSelectBox'ın beklediği option listesine çevirir (çoklu seçim). */
const toSelectedOptions = (values: string[], options: ISelectOption[]): ISelectOption[] =>
  values.map(value => options.find(option => String(option.value) === value) ?? { value, label: value })

/** Virgül, noktalı virgül, boşluk ya da satır sonuyla ayrılmış serbest metni tekil değer listesine çevirir. */
const parseList = (text: string): string[] =>
  Array.from(new Set(String(text ?? '').split(/[\s,;]+/).map(value => value.trim()).filter(Boolean)))

/** Seçim listesi boşsa servise hiç gönderilmez. */
const listOrUndefined = (values: string[]) => (values.length ? values : undefined)

/** Malzeme no SAP'de 18 hane ve baştaki 0'larla tutulur; yalnız rakamdan oluşan girişler tamamlanır. */
const toMaterial18 = (value: string) => (/^\d+$/.test(value) ? value.padStart(18, '0') : value)

/** Kaydedilmiş string değeri IPSelectBox'ın beklediği seçili option objesine çevirir. */
const toSelectedOption = (value: string, options: ISelectOption[]): ISelectOption | null =>
  value ? options.find(option => String(option.value) === value) ?? { value, label: value } : null

const pad2 = (value: number) => String(value).padStart(2, '0')

const toNumber = (value: any): number => {
  if (value === null || value === undefined || value === '') return 0
  if (typeof value === 'number') return isNaN(value) ? 0 : value

  const text = String(value).trim().replace(/\s/g, '')
  // 1.234,56 (TR) ve 1234.56 (EN) formatlarının ikisini de destekle
  const normalized = text.includes(',') ? text.replace(/\./g, '').replace(',', '.') : text
  const parsed = Number(normalized)
  return isNaN(parsed) ? 0 : parsed
}

/** ISO, yyyyMMdd, dd.MM.yyyy ve dd/MM/yyyy formatlarını destekler. */
const toDate = (value: any): Date | null => {
  if (!value) return null
  if (value instanceof Date) return isNaN(value.getTime()) ? null : value

  const text = String(value).trim()
  if (!text) return null

  const compact = /^(\d{4})(\d{2})(\d{2})$/.exec(text)
  if (compact) return new Date(Number(compact[1]), Number(compact[2]) - 1, Number(compact[3]))

  const dmy = /^(\d{1,2})[./-](\d{1,2})[./-](\d{4})/.exec(text)
  if (dmy) return new Date(Number(dmy[3]), Number(dmy[2]) - 1, Number(dmy[1]))

  const ymd = /^(\d{4})[./-](\d{1,2})[./-](\d{1,2})/.exec(text)
  if (ymd) return new Date(Number(ymd[1]), Number(ymd[2]) - 1, Number(ymd[3]))

  const parsed = new Date(text)
  return isNaN(parsed.getTime()) ? null : parsed
}

const formatDate = (date: Date | null) =>
  date ? `${pad2(date.getDate())}.${pad2(date.getMonth() + 1)}.${date.getFullYear()}` : '-'

/** Servis tarih formatı: yyyyMMdd */
const toApiDate = (value: string) => value.replace(/\D/g, '')

/** Servise gönderilen bayi kodu 10 hane olacak şekilde başına 0 eklenir. */
const toDealerCode10 = (value: string) => {
  const digits = String(value ?? '').replace(/\D/g, '')
  return digits ? digits.padStart(10, '0') : ''
}

const formatNumber = (value: number) => (Number(value) || 0).toLocaleString('tr-TR')

const percentValue = (activation: number, sales: number) => (sales ? Math.round((activation / sales) * 100) : 0)

/**
 * İki tarih arasındaki tüm gün (veya ay) kovalarını sırayla üretir.
 * Veri gelmeyen tarihlerin de grafikte 0 olarak görünmesi için kullanılır.
 */
const buildTrendBuckets = (start: Date, end: Date, byDay: boolean): { key: string; label: string }[] => {
  const buckets: { key: string; label: string }[] = []

  if (byDay) {
    const cursor = new Date(start.getFullYear(), start.getMonth(), start.getDate())
    const last = new Date(end.getFullYear(), end.getMonth(), end.getDate()).getTime()
    // Hatalı/çok geniş aralıkta sonsuz döngüye girmemek için üst sınır
    let guard = 0
    while (cursor.getTime() <= last && guard++ < 400) {
      const month = pad2(cursor.getMonth() + 1)
      buckets.push({
        key: `${cursor.getFullYear()}${month}${pad2(cursor.getDate())}`,
        label: `${pad2(cursor.getDate())}.${month}`,
      })
      cursor.setDate(cursor.getDate() + 1)
    }
  } else {
    const cursor = new Date(start.getFullYear(), start.getMonth(), 1)
    const last = new Date(end.getFullYear(), end.getMonth(), 1).getTime()
    let guard = 0
    while (cursor.getTime() <= last && guard++ < 240) {
      const month = pad2(cursor.getMonth() + 1)
      buckets.push({ key: `${cursor.getFullYear()}${month}`, label: `${month}.${cursor.getFullYear()}` })
      cursor.setMonth(cursor.getMonth() + 1)
    }
  }

  return buckets
}

const CURRENCY_SYMBOLS: Record<string, string> = { TRY: '₺', TL: '₺', USD: '$', EUR: '€', GBP: '£' }

/** Tutarı para birimi sembolü/koduyla biçimlendirir: 1234.5, 'USD' → '1.234,50 $' */
const formatCurrencyAmount = (value: number, currency: string) => {
  const code = String(currency ?? '').trim().toUpperCase()
  const amount = (Number(value) || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
  const symbol = CURRENCY_SYMBOLS[code]
  return symbol ? `${amount} ${symbol}` : `${amount} ${code}`.trim()
}

/** Net tutarları para birimi bazında toplar. Boş para birimi 'DİĞER' altında toplanır. */
const sumNetByCurrency = (list: IDashboardRow[]): Record<string, number> => {
  const map: Record<string, number> = {}
  list.forEach(row => {
    const code = String(row.currency ?? '').trim().toUpperCase() || 'DİĞER'
    map[code] = (map[code] ?? 0) + row.netAmount
  })
  return map
}

/** { TRY: 1234.5, USD: 500 } → '1.234,50 ₺ + 500,00 $' (farklı para birimleri toplanmaz). */
const formatCurrencyTotals = (map: Record<string, number>): string => {
  const entries = Object.entries(map).filter(([, total]) => total !== 0)
  if (entries.length === 0) return formatCurrencyAmount(0, 'TRY')
  return entries
    .sort((a, b) => b[1] - a[1])
    .map(([currency, total]) => formatCurrencyAmount(total, currency))
    .join(' + ')
}

/** SAP/müşteri kodlarını (0000123456 - 123456) karşılaştırılabilir hale getirir. */
const normalizeDealerCode = (value: any) =>
  String(value ?? '')
    .replace(/\D/g, '')
    .replace(/^0+/, '')

const sortText = (a: string, b: string) => a.localeCompare(b, 'tr')

/** Filtre servisinden gelen liste, benzersiz ve sıralı seçeneklere dönüştürülür. */
const toOptions = (values: (string | undefined)[]): string[] =>
  Array.from(new Set(values.map(value => String(value ?? '').trim()).filter(Boolean))).sort(sortText)

/** Servis modeli (VodafoneGeneralReport) alanları doğrudan kullanılır. */
const mapRow = (raw: VodafoneGeneralReport): IDashboardRow => ({
  salesDate: toDate(raw.SATIS_TARIHI),
  activationDate: toDate(raw.AKTIVASYON_TARIHI),
  brand: raw.MARKA ?? '',
  model: raw.MODEL ?? '',
  urunTipi: raw.URUN_TIPI ?? '',
  urunKodu: raw.URUN_KODU ?? '',
  malzemeNo: raw.MALZEME_NO ?? '',
  kota: raw.KOTA_NO ?? '',
  kotaAciklama: raw.KOTA_ACIKLAMASI ?? '',
  urunTuru: raw.URUN_TURU ?? '',
  hiyerarsi: raw.URUN_HIYERARSISI ?? '',
  hiyerarsiAciklama: raw.URUN_HIYERARSISI_ACIKLAMA ?? '',
  dealerCode: raw.MUSTERI_KODU ?? '',
  dealerName: raw.MUSTERI_ADI ?? '',
  salesRep: raw.SATIS_TEMSILCISI ?? '',
  channelManager: raw.KANAL_SORUMLUSU ?? '',
  regionManager: raw.BOLGE_SORUMLUSU ?? '',
  currency: raw.PARA_BIRIMI ?? '',
  sales: toNumber(raw.SATIS_ADEDI),
  netAmount: toNumber(raw.SATIS_NET_TUTAR),
  temlik: toNumber(raw.TEMLIK_ADEDI),
  kontrat: toNumber(raw.PESIN_KONTRAT_ADEDI),
  mikro: toNumber(raw.MIKRO_KREDI_ADEDI),
  activation: toNumber(raw.TOPLAM_AKTIVASYON),
  vdfAktivasyonTutari: toNumber(raw.VDF_AKTIVASYON_TUTARI),
  aktivasyonTutari: toNumber(raw.AKTIVASYON_TUTARI),
})

const styles: { [key: string]: React.CSSProperties } = {
  page: { backgroundColor: '#f1f5f9', padding: '1rem', borderRadius: '8px' },
  card: {
    backgroundColor: '#ffffff',
    borderRadius: '10px',
    boxShadow: '0 1px 4px rgba(15, 23, 42, 0.08)',
    padding: '1rem 1.25rem',
    height: '100%',
  },
  kpiLabel: { fontSize: '0.75rem', color: '#64748b', display: 'block', marginBottom: '0.25rem' },
  kpiValue: { fontSize: '1.5rem', fontWeight: 700, color: '#0f172a', margin: 0 },
  cardTitle: { fontSize: '1rem', fontWeight: 600, color: '#0f172a', marginBottom: '1rem' },
  // Tarih ekseni butonları: tema sınıfları hover'da beyaz-üstüne-beyaz verdiği için renkler burada sabit.
  axisButton: {
    border: '1px solid #cbd5e1',
    padding: '0.35rem 0.85rem',
    fontSize: '0.8rem',
    fontWeight: 600,
    lineHeight: 1.4,
    cursor: 'pointer',
    transition: 'background-color .15s ease, color .15s ease',
  },
  listRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '0.5rem 0',
    borderBottom: '1px solid #e2e8f0',
  },
}

function VodafoneGeneralDashboard(props: BaseComponentWithContextProps) {
  const [loading, setLoading] = useState(false)
  const [searched, setSearched] = useState(false)
  const [filterData, setFilterData] = useState<VodafoneGeneralReportFilter | null>(null)
  const [report, setReport] = useState<IDashboardRow[]>([])

  // Tek tarih aralığı zorunludur; hangi tarihe uygulanacağını rapor tarih ekseni butonları belirler.
  const [startDate, setStartDate] = useState('')
  const [endDate, setEndDate] = useState('')

  // Grafik/tablo kırılımları sadece "Raporu Getir" ile uygulanan bu değerlere göre hesaplanır;
  // tarih alanlarını değiştirmek tek başına grafiği güncellemez.
  const [appliedStartDate, setAppliedStartDate] = useState('')
  const [appliedEndDate, setAppliedEndDate] = useState('')

  // Çoklu seçim filtreleri (Ekim 2026): boş liste = filtre yok.
  const [marka, setMarka] = useState<string[]>([])
  const [kotaNo, setKotaNo] = useState<string[]>([])
  const [model, setModel] = useState<string[]>([])
  const [depo, setDepo] = useState<string[]>([])
  const [urunTuru, setUrunTuru] = useState<string[]>([])
  const [hiyerarsi, setHiyerarsi] = useState<string[]>([])
  const [satisTemsilcisi, setSatisTemsilcisi] = useState<string[]>([])
  const [kanalSorumlusu, setKanalSorumlusu] = useState<string[]>([])
  const [bolgeSorumlusu, setBolgeSorumlusu] = useState<string[]>([])
  // Serbest metin alanlarında birden çok değer virgülle girilir.
  const [bayiSap, setBayiSap] = useState('')
  const [urunKodu, setUrunKodu] = useState('')
  const [malzemeNo, setMalzemeNo] = useState('')
  const [activationType, setActivationType] = useState<ActivationType>('')
  const [demo, setDemo] = useState<DemoFilter>('')

  // Grafikler varsayılan olarak yan yana; işaretlenince alt alta (tam genişlik) gösterilir.
  const [stackedCharts, setStackedCharts] = useState(false)

  // Raporun tarih ekseni; kullanıcı filtre panelinin altındaki buton grubuyla değiştirir.
  const [reportAxis, setReportAxis] = useState<ReportAxis>('tum')
  const [hoveredAxis, setHoveredAxis] = useState<ReportAxis | null>(null)

  const showError = (title: string, err: any) => {
    const msg =
      err?.response?.data?.error ||
      err?.response?.data?.message ||
      err?.message ||
      err?.description ||
      err ||
      'Beklenmeyen bir hata oluştu.'
    Swal.fire({ icon: 'error', title, text: String(msg) })
  }

  /** Bayi adı yalnızca raporun kendi MUSTERI_ADI alanından alınır. */
  const reportNameMap = useMemo(() => {
    const map: Record<string, string> = {}
    report.forEach(row => {
      const key = normalizeDealerCode(row.dealerCode)
      if (key && row.dealerName) map[key] = row.dealerName
    })
    return map
  }, [report])

  const getDealerName = useCallback(
    (dealerCode: string) => {
      const key = normalizeDealerCode(dealerCode)
      return reportNameMap[key] || key || '-'
    },
    [reportNameMap]
  )

  const fetchFilters = useCallback(async () => {
    try {
      const api = new GetVodafoneGeneralReportFilterApi({})
      const data = await api.getPKIMEIData()
      setFilterData(data)
    } catch (err) {
      showError('Filtreler getirilemedi', err)
    }
  }, [])

  useEffect(() => {
    fetchFilters()
  }, [fetchFilters])

  // Filtre seçenekleri doğrudan filtre servisinin (VodafoneGeneralReportFilter) alanlarından üretilir.
  const markaOptions = useMemo(() => toOptions((filterData?.Marka ?? []).map(item => item.MARKA)), [filterData])

  const kotaOptions = useMemo(() => toOptions((filterData?.KotaNo ?? []).map(item => item.KOTA_NO)), [filterData])

  const modelOptions = useMemo(() => toOptions((filterData?.Model ?? []).map(item => item.MODEL)), [filterData])

  const depoOptions = useMemo(() => toOptions((filterData?.Depo ?? []).map(item => item.DEPO)), [filterData])

  const markaSelectOptions = useMemo(() => toSelectOptions(markaOptions), [markaOptions])
  const kotaSelectOptions = useMemo(() => toSelectOptions(kotaOptions), [kotaOptions])
  const modelSelectOptions = useMemo(() => toSelectOptions(modelOptions), [modelOptions])
  const depoSelectOptions = useMemo(() => toSelectOptions(depoOptions), [depoOptions])

  const urunTuruSelectOptions = useMemo(
    () => toSelectOptions(toOptions((filterData?.UrunTuru ?? []).map(item => item.URUN_TURU))),
    [filterData]
  )

  // Ürün hiyerarşisi: değer kod (SAP'de önek olarak süzülür), etiket açıklama + kod.
  const hiyerarsiSelectOptions = useMemo(() => {
    const seen = new Set<string>()
    return (filterData?.UrunHiyerarsisi ?? [])
      .filter(item => item.KOD && !seen.has(item.KOD) && !!seen.add(item.KOD))
      .map(item => ({ value: item.KOD, label: item.ACIKLAMA ? `${item.ACIKLAMA} (${item.KOD})` : item.KOD }))
      .sort((a, b) => sortText(a.label, b.label))
  }, [filterData])

  // Sorumlular: değer iş ortağı kodu (LIFNR), etiket ad.
  const toSorumluOptions = (list?: { KOD: string; AD: string }[]): ISelectOption[] => {
    const seen = new Set<string>()
    return (list ?? [])
      .filter(item => item.KOD && !seen.has(item.KOD) && !!seen.add(item.KOD))
      .map(item => ({ value: item.KOD, label: item.AD || item.KOD }))
      .sort((a, b) => sortText(String(a.label), String(b.label)))
  }
  const temsilciSelectOptions = useMemo(() => toSorumluOptions(filterData?.SatisTemsilcisi), [filterData])
  const kanalSelectOptions = useMemo(() => toSorumluOptions(filterData?.KanalSorumlusu), [filterData])
  const bolgeSelectOptions = useMemo(() => toSorumluOptions(filterData?.BolgeSorumlusu), [filterData])

  const toValues = (options: ISelectOption[] | null | undefined) => (options ?? []).map(option => String(option.value))

  const getReport = async () => {
    // Tarih aralığı zorunlu: aralık verilmezse servis tüm aktivasyon ve fatura geçmişini tarar.
    if (!startDate || !endDate) {
      Swal.fire({
        icon: 'warning',
        title: 'Eksik bilgi',
        text: 'Tarih aralığı zorunludur; başlangıç ve bitiş tarihini birlikte girin.',
      })
      return
    }

    if (toApiDate(startDate) > toApiDate(endDate)) {
      Swal.fire({ icon: 'warning', title: 'Hatalı tarih', text: 'Başlangıç tarihi bitişinden büyük olamaz.' })
      return
    }

    const request: VodafoneGeneralReportRequest = {
      StartDate: toApiDate(startDate),
      EndDate: toApiDate(endDate),
      Marka: listOrUndefined(marka),
      KotaNo: listOrUndefined(kotaNo),
      Depo: listOrUndefined(depo),
      BayiKodu: listOrUndefined(parseList(bayiSap).map(toDealerCode10).filter(Boolean)),
      Model: listOrUndefined(model),
      UrunKodu: listOrUndefined(parseList(urunKodu)),
      MalzemeNo: listOrUndefined(parseList(malzemeNo).map(toMaterial18)),
      UrunTuru: listOrUndefined(urunTuru),
      UrunHiyerarsisi: listOrUndefined(hiyerarsi),
      SatisTemsilcisi: listOrUndefined(satisTemsilcisi),
      KanalSorumlusu: listOrUndefined(kanalSorumlusu),
      BolgeSorumlusu: listOrUndefined(bolgeSorumlusu),
    }

    // "Tümü" (demo === '') seçiliyken IsDemo hiç gönderilmez; yalnızca Evet/Hayır iletilir.
    if (demo === 'evet') request.IsDemo = true
    else if (demo === 'hayir') request.IsDemo = false

    setLoading(true)
    props.showLoading()
    try {
      const api = new GetVodafoneGeneralReportApi({})
      const data = await api.getVodafoneGeneralReport(request)
      setReport((Array.isArray(data) ? data : []).map(mapRow))
      setSearched(true)
      setAppliedStartDate(startDate)
      setAppliedEndDate(endDate)
    } catch (err) {
      showError('Rapor getirilemedi', err)
      setReport([])
    } finally {
      setLoading(false)
      props.hideLoading()
    }
  }

  /** Tarih, "Raporu Getir" ile uygulanan aralıkta mı (gün bazında, yyyyMMdd karşılaştırması). */
  const inAppliedRange = useCallback(
    (date: Date | null) => {
      if (!date) return false
      if (!appliedStartDate || !appliedEndDate) return true
      const key = `${date.getFullYear()}${pad2(date.getMonth() + 1)}${pad2(date.getDate())}`
      return key >= toApiDate(appliedStartDate) && key <= toApiDate(appliedEndDate)
    },
    [appliedStartDate, appliedEndDate]
  )

  /**
   * Tablo/Excel satırları. Servis satış VEYA aktivasyon tarihi aralıkta olan satırları döndürür; tek eksenli
   * modlarda o tarihi aralık dışında kalan satırlar düşer. Servis tarafında karşılığı olmayan aktivasyon türü
   * filtresi de burada uygulanır.
   */
  const rows = useMemo(() => {
    return report.filter(row => {
      if (reportAxis === 'satis' && !inAppliedRange(row.salesDate)) return false
      if (reportAxis === 'aktivasyon' && !inAppliedRange(row.activationDate)) return false

      if (activationType === 'temlik' && !row.temlik) return false
      if (activationType === 'kontrat' && !row.kontrat) return false
      if (activationType === 'mikro' && !row.mikro) return false

      return true
    })
  }, [report, activationType, reportAxis, inAppliedRange])

  /**
   * Kutucuk, sıralama ve grafiklerin hesaplandığı satırlar. 'tum' modunda satış metrikleri yalnız satış tarihi,
   * aktivasyon metrikleri yalnız aktivasyon tarihi aralıktaysa sayılır; aksi halde aralık dışı satış/aktivasyon
   * toplamları şişirirdi. Tek eksenli modlarda satırlar olduğu gibi kullanılır.
   */
  const metricRows = useMemo(() => {
    if (reportAxis !== 'tum') return rows

    return rows.map(row => {
      const salesIn = inAppliedRange(row.salesDate)
      const activationIn = inAppliedRange(row.activationDate)
      if (salesIn && activationIn) return row

      return {
        ...row,
        sales: salesIn ? row.sales : 0,
        netAmount: salesIn ? row.netAmount : 0,
        activation: activationIn ? row.activation : 0,
        temlik: activationIn ? row.temlik : 0,
        kontrat: activationIn ? row.kontrat : 0,
        mikro: activationIn ? row.mikro : 0,
        vdfAktivasyonTutari: activationIn ? row.vdfAktivasyonTutari : 0,
        aktivasyonTutari: activationIn ? row.aktivasyonTutari : 0,
      }
    })
  }, [rows, reportAxis, inAppliedRange])

  const summary = useMemo(() => {
    const totalSales = metricRows.reduce((sum, row) => sum + row.sales, 0)
    const totalActivation = metricRows.reduce((sum, row) => sum + row.activation, 0)
    const dealerCodes = new Set(metricRows.map(row => normalizeDealerCode(row.dealerCode)).filter(Boolean))

    return {
      totalSales,
      totalActivation,
      rate: percentValue(totalActivation, totalSales),
      dealerCount: dealerCodes.size,
      netByCurrency: sumNetByCurrency(metricRows),
      vdfAktivasyonTutari: metricRows.reduce((sum, row) => sum + row.vdfAktivasyonTutari, 0),
      aktivasyonTutari: metricRows.reduce((sum, row) => sum + row.aktivasyonTutari, 0),
    }
  }, [metricRows])

  /** Verilen kırılıma göre satış/aktivasyon toplamlarını hesaplar. */
  const groupBy = useCallback(
    (list: IDashboardRow[], selector: (row: IDashboardRow) => { key: string; label: string } | null) => {
      const map = new Map<string, IGroupItem>()

      list.forEach(row => {
        const group = selector(row)
        if (!group || !group.key) return

        const current =
          map.get(group.key) ??
          ({ key: group.key, label: group.label, sales: 0, activation: 0, temlik: 0, kontrat: 0, mikro: 0 } as IGroupItem)

        current.sales += row.sales
        current.activation += row.activation
        current.temlik += row.temlik
        current.kontrat += row.kontrat
        current.mikro += row.mikro
        map.set(group.key, current)
      })

      return Array.from(map.values())
    },
    []
  )

  const brandStats = useMemo(
    () => groupBy(metricRows, row => (row.brand ? { key: row.brand, label: row.brand } : null)).sort((a, b) => b.sales - a.sales),
    [metricRows, groupBy]
  )

  const kotaStats = useMemo(
    () =>
      groupBy(metricRows, row => (row.kota ? { key: row.kota, label: row.kota } : null))
        .filter(item => item.sales > 0)
        .sort((a, b) => percentValue(b.activation, b.sales) - percentValue(a.activation, a.sales)),
    [metricRows, groupBy]
  )

  const dealerStats = useMemo(
    () =>
      groupBy(metricRows, row =>
        row.dealerCode ? { key: row.dealerCode, label: getDealerName(row.dealerCode) } : null
      ).sort((a, b) => b.activation - a.activation),
    [metricRows, groupBy, getDealerName]
  )

  const salesRepStats = useMemo(
    () =>
      groupBy(metricRows, row => (row.salesRep ? { key: row.salesRep, label: row.salesRep } : null)).sort(
        (a, b) => b.activation - a.activation
      ),
    [metricRows, groupBy]
  )

  const channelStats = useMemo(
    () =>
      groupBy(metricRows, row => (row.channelManager ? { key: row.channelManager, label: row.channelManager } : null)).sort(
        (a, b) => b.activation - a.activation
      ),
    [metricRows, groupBy]
  )

  const regionStats = useMemo(
    () =>
      groupBy(metricRows, row => (row.regionManager ? { key: row.regionManager, label: row.regionManager } : null)).sort(
        (a, b) => b.activation - a.activation
      ),
    [metricRows, groupBy]
  )

  const trendRangeStart = appliedStartDate
  const trendRangeEnd = appliedEndDate

  const reportAxisNote = useMemo(
    () => REPORT_AXIS_OPTIONS.find(option => option.value === reportAxis)?.note ?? '',
    [reportAxis]
  )

  /** Seçilen aralık 2 aydan kısaysa gün, uzunsa ay kırılımı gösterilir. "Raporu Getir" ile uygulanan aralığa göre hesaplanır. */
  const groupByDay = useMemo(() => {
    const start = toDate(trendRangeStart)
    const end = toDate(trendRangeEnd)
    if (!start || !end) return false
    return (end.getTime() - start.getTime()) / 86400000 <= 62
  }, [trendRangeStart, trendRangeEnd])

  const trend = useMemo(() => {
    const bucketOf = (date: Date | null) => {
      if (!date) return null

      const year = date.getFullYear()
      const month = pad2(date.getMonth() + 1)

      return groupByDay
        ? { key: `${year}${month}${pad2(date.getDate())}`, label: `${pad2(date.getDate())}.${month}` }
        : { key: `${year}${month}`, label: `${month}.${year}` }
    }

    // Satış ve aktivasyon metrikleri ayrı ayrı kovalanır: 'tum' modunda her biri kendi tarihine,
    // tek eksenli modlarda ikisi de seçilen tarihe yazılır. Bir satır hem satış hem aktivasyon
    // taşıyorsa 'tum' modunda iki farklı kovaya katkı verebilir.
    const found = new Map<string, IGroupItem>()
    const bucketItem = (bucket: { key: string; label: string }) => {
      const current =
        found.get(bucket.key) ?? ({ ...bucket, sales: 0, activation: 0, temlik: 0, kontrat: 0, mikro: 0 } as IGroupItem)
      found.set(bucket.key, current)
      return current
    }

    metricRows.forEach(row => {
      const salesDate = reportAxis === 'aktivasyon' ? row.activationDate : row.salesDate
      const activationDate = reportAxis === 'satis' ? row.salesDate : row.activationDate
      // Aralık dışındaki tarih ('tum' modunda metriği zaten 0) grafiğe boş kova açmaz.
      const salesBucket = inAppliedRange(salesDate) ? bucketOf(salesDate) : null
      const activationBucket = inAppliedRange(activationDate) ? bucketOf(activationDate) : null

      if (salesBucket) bucketItem(salesBucket).sales += row.sales

      if (activationBucket) {
        const target = bucketItem(activationBucket)
        target.activation += row.activation
        target.temlik += row.temlik
        target.kontrat += row.kontrat
        target.mikro += row.mikro
      }
    })

    const buckets = Array.from(found.values())

    // Seçilen aralıktaki tüm tarihler eklenir; veri gelmeyen tarihler 0 değerlerle grafikte yer alır.
    const start = toDate(trendRangeStart)
    const end = toDate(trendRangeEnd)
    const rangeBuckets = start && end ? buildTrendBuckets(start, end, groupByDay) : []

    const filled: IGroupItem[] = rangeBuckets.map(
      bucket => found.get(bucket.key) ?? { ...bucket, sales: 0, activation: 0, temlik: 0, kontrat: 0, mikro: 0 }
    )

    // Aralık dışında kalan (ör. tarih filtresi girilmemişse tüm) kovalar kaybolmasın
    const rangeKeys = new Set(rangeBuckets.map(bucket => bucket.key))
    buckets.forEach(item => {
      if (!rangeKeys.has(item.key)) filled.push(item)
    })

    return filled.sort((a, b) => a.key.localeCompare(b.key))
  }, [metricRows, reportAxis, inAppliedRange, groupByDay, trendRangeStart, trendRangeEnd])

  const trendChartData = useMemo(
    () => ({
      labels: trend.map(item => item.label),
      datasets: [
        {
          label: 'Satış',
          data: trend.map(item => item.sales),
          borderColor: COLORS.blue,
          backgroundColor: COLORS.blue,
          tension: 0.35,
          yAxisID: 'y',
        },
        {
          label: 'Aktivasyon',
          data: trend.map(item => item.activation),
          borderColor: COLORS.green,
          backgroundColor: COLORS.green,
          tension: 0.35,
          yAxisID: 'y',
        },
        {
          // Oran adet eksenine göre çok küçük kaldığı için sağdaki % eksenine bağlanır.
          label: 'Oran (%)',
          data: trend.map(item => percentValue(item.activation, item.sales)),
          borderColor: COLORS.orange,
          backgroundColor: COLORS.orange,
          tension: 0.35,
          borderDash: [6, 4],
          yAxisID: 'yOran',
        },
      ],
    }),
    [trend]
  )

  const activationChartData = useMemo(
    () => ({
      labels: trend.map(item => item.label),
      datasets: [
        { label: 'Temlik', data: trend.map(item => item.temlik), backgroundColor: COLORS.blue },
        { label: 'Peşin Kontrat', data: trend.map(item => item.kontrat), backgroundColor: COLORS.green },
        { label: 'Mikro Kredi', data: trend.map(item => item.mikro), backgroundColor: COLORS.orange },
      ],
    }),
    [trend]
  )

  const lineOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      interaction: { mode: 'index' as const, intersect: false },
      plugins: {
        legend: { position: 'bottom' as const },
        tooltip: {
          callbacks: {
            // Oran satırı tooltip'te de yüzde olarak gösterilir.
            label: (context: any) =>
              context.dataset.yAxisID === 'yOran'
                ? `${context.dataset.label}: %${context.parsed.y}`
                : `${context.dataset.label}: ${formatNumber(context.parsed.y)}`,
          },
        },
      },
      scales: {
        // Sol eksen adet (satış/aktivasyon), sağ eksen yüzde (oran) içindir.
        y: { beginAtZero: true, position: 'left' as const, title: { display: true, text: 'Adet' } },
        yOran: {
          beginAtZero: true,
          position: 'right' as const,
          suggestedMax: 100,
          title: { display: true, text: 'Oran (%)' },
          grid: { drawOnChartArea: false },
          ticks: { callback: (value: any) => `%${value}` },
        },
      },
    }),
    []
  )

  const barOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      plugins: { legend: { position: 'bottom' as const } },
      scales: { x: { stacked: true }, y: { stacked: true, beginAtZero: true } },
    }),
    []
  )

  // Alt alta modda grafikler tam genişlik kaplar; yan yana modda ekranı ikiye böler.
  const chartColumnClass = stackedCharts ? 'col-12' : 'col-lg-6'
  const chartHeight = stackedCharts ? '340px' : '300px'

  const columns = useMemo(
    () => [
      { Header: 'Satış Tarihi', accessor: (row: IDashboardRow) => formatDate(row.salesDate) },
      { Header: 'Aktivasyon Tarihi', accessor: (row: IDashboardRow) => formatDate(row.activationDate) },
      { Header: 'Marka', accessor: (row: IDashboardRow) => row.brand || '-' },
      { Header: 'Model', accessor: (row: IDashboardRow) => row.model || '-' },
      { Header: 'Ürün Tipi', accessor: (row: IDashboardRow) => row.urunTipi || '-' },
      { Header: 'Kota', accessor: (row: IDashboardRow) => row.kota || '-' },
      { Header: 'Kota Açıklaması', accessor: (row: IDashboardRow) => row.kotaAciklama || '-' },
      { Header: 'Ürün Türü', accessor: (row: IDashboardRow) => row.urunTuru || '-' },
      { Header: 'Ürün Hiyerarşisi', accessor: (row: IDashboardRow) => row.hiyerarsiAciklama || row.hiyerarsi || '-' },
      { Header: 'Bayi Kodu', accessor: (row: IDashboardRow) => normalizeDealerCode(row.dealerCode) },
      { Header: 'Bayi Adı', accessor: (row: IDashboardRow) => getDealerName(row.dealerCode) },
      { Header: 'Satış Temsilcisi', accessor: (row: IDashboardRow) => row.salesRep || '-' },
      { Header: 'Kanal Sorumlusu', accessor: (row: IDashboardRow) => row.channelManager || '-' },
      { Header: 'Bölge Sorumlusu', accessor: (row: IDashboardRow) => row.regionManager || '-' },
      { Header: 'Ürün Kodu', accessor: (row: IDashboardRow) => row.urunKodu || '-' },
      { Header: 'Malzeme No', accessor: (row: IDashboardRow) => row.malzemeNo || '-' },
      { Header: 'Satış', accessor: 'sales' },
      { Header: 'Net Tutar', accessor: (row: IDashboardRow) => formatCurrencyAmount(row.netAmount, row.currency) },
      { Header: 'Para Birimi', accessor: (row: IDashboardRow) => row.currency || '-' },
      { Header: 'Temlik', accessor: 'temlik' },
      { Header: 'Peşin Kontrat', accessor: 'kontrat' },
      { Header: 'Mikro Kredi', accessor: 'mikro' },
      { Header: 'Toplam Aktivasyon', accessor: 'activation' },
      {
        Header: 'VDF Aktivasyon Tutarı',
        accessor: (row: IDashboardRow) => formatCurrencyAmount(row.vdfAktivasyonTutari, 'TRY'),
      },
      { Header: 'Aktivasyon Tutarı', accessor: (row: IDashboardRow) => formatCurrencyAmount(row.aktivasyonTutari, 'TRY') },
      {
        Header: 'Aktivasyon %',
        accessor: (row: IDashboardRow) => percentValue(row.activation, row.sales),
        Cell: ({ value }: any) => `%${value}`,
      },
    ],
    [getDealerName]
  )

  const exportRows = useCallback(
    () =>
      rows.map(row => ({
        'Satış Tarihi': formatDate(row.salesDate),
        'Aktivasyon Tarihi': formatDate(row.activationDate),
        Marka: row.brand,
        Model: row.model,
        'Ürün Tipi': row.urunTipi,
        Kota: row.kota,
        'Kota Açıklaması': row.kotaAciklama,
        'Ürün Türü': row.urunTuru,
        'Ürün Hiyerarşisi': row.hiyerarsi,
        'Ürün Hiyerarşisi Açıklaması': row.hiyerarsiAciklama,
        'Bayi Kodu': normalizeDealerCode(row.dealerCode),
        'Bayi Adı': getDealerName(row.dealerCode),
        'Satış Temsilcisi': row.salesRep,
        'Kanal Sorumlusu': row.channelManager,
        'Bölge Sorumlusu': row.regionManager,
        'Ürün Kodu': row.urunKodu,
        'Malzeme No': row.malzemeNo,
        'Satış': row.sales,
        'Net Tutar': row.netAmount,
        'Para Birimi': row.currency,
        Temlik: row.temlik,
        'Peşin Kontrat': row.kontrat,
        'Mikro Kredi': row.mikro,
        'Toplam Aktivasyon': row.activation,
        'VDF Aktivasyon Tutarı (TRY)': row.vdfAktivasyonTutari,
        'Aktivasyon Tutarı (TRY)': row.aktivasyonTutari,
        'Aktivasyon %': percentValue(row.activation, row.sales),
      })),
    [rows, getDealerName]
  )

  const onExportExcel = () => {
    if (rows.length === 0) {
      Swal.fire({ icon: 'info', title: 'Veri yok', text: 'Dışa aktarılacak kayıt bulunamadı.' })
      return
    }
    exportToXLSX(exportRows(), 'Vodafone_Genel_Rapor')
  }

  const kpiCards: { label: string; value: string; color: string; small?: boolean }[] = [
    { label: 'Toplam Satış', value: formatNumber(summary.totalSales), color: '#0f172a' },
    { label: 'Net Satış Tutarı', value: formatCurrencyTotals(summary.netByCurrency), color: '#0f172a', small: true },
    { label: 'Toplam Aktivasyon', value: formatNumber(summary.totalActivation), color: '#0f172a' },
    { label: 'Aktivasyon Oranı', value: `%${summary.rate}`, color: COLORS.green },
    {
      label: 'VDF Aktivasyon Tutarı',
      value: formatCurrencyAmount(summary.vdfAktivasyonTutari, 'TRY'),
      color: '#0f172a',
      small: true,
    },
    {
      label: 'Aktivasyon Tutarı (A1)',
      value: formatCurrencyAmount(summary.aktivasyonTutari, 'TRY'),
      color: '#0f172a',
      small: true,
    },
    { label: 'Aktif Bayi', value: formatNumber(summary.dealerCount), color: '#0f172a' },
    { label: 'En İyi Marka', value: brandStats[0]?.label ?? '-', color: '#0f172a' },
    { label: 'En İyi Kota', value: kotaStats[0]?.label ?? '-', color: '#0f172a' },
  ]

  return (
    <div className='content col-lg-12 m-auto'>
      <div style={styles.page}>
        <div className='mb-4'>
          <h4 style={{ fontWeight: 700, color: '#0f172a', marginBottom: '0.25rem' }}>
            Vodafone Satış &amp; Aktivasyon Performans Dashboard
          </h4>
        </div>

        {/* Filtreler */}
        <div style={{ ...styles.card, marginBottom: '1rem' }}>
          <div style={styles.cardTitle}>Filtreler</div>

          <div className='row g-3 align-items-end'>
            <div className='col-md-2'>
              <label className='form-label'>Başlangıç Tarihi</label>
              <InputCampaign
                type='date'
                id='vdfStartDate'
                name='vdfStartDate'
                label=''
                value={startDate}
                onChange={e => setStartDate(e.target.value)}
                isActive
              />
            </div>
            <div className='col-md-2'>
              <label className='form-label'>Bitiş Tarihi</label>
              <InputCampaign
                type='date'
                id='vdfEndDate'
                name='vdfEndDate'
                label=''
                value={endDate}
                onChange={e => setEndDate(e.target.value)}
                isActive
              />
            </div>
            <div className='col-md-4'>
              <label className='form-label'>Depo</label>
              <IPSelectBox
                isMulti
                isClearable
                placeholder='Tüm Depolar'
                options={depoSelectOptions}
                value={toSelectedOptions(depo, depoSelectOptions)}
                onChangeMulti={options => setDepo(toValues(options))}
                isDisabled={loading}
              />
            </div>
            <div className='col-md-4'>
              <label className='form-label'>Kota</label>
              <IPSelectBox
                isMulti
                isClearable
                placeholder='Tüm Kotalar'
                options={kotaSelectOptions}
                value={toSelectedOptions(kotaNo, kotaSelectOptions)}
                onChangeMulti={options => setKotaNo(toValues(options))}
                isDisabled={loading}
              />
            </div>
            <div className='col-md-3'>
              <label className='form-label'>Marka</label>
              <IPSelectBox
                isMulti
                isClearable
                placeholder='Tüm Markalar'
                options={markaSelectOptions}
                value={toSelectedOptions(marka, markaSelectOptions)}
                onChangeMulti={options => setMarka(toValues(options))}
                isDisabled={loading}
              />
            </div>
            <div className='col-md-3'>
              <label className='form-label'>Model</label>
              <IPSelectBox
                isMulti
                isClearable
                placeholder='Tüm Modeller'
                options={modelSelectOptions}
                value={toSelectedOptions(model, modelSelectOptions)}
                onChangeMulti={options => setModel(toValues(options))}
                isDisabled={loading}
              />
            </div>
            <div className='col-md-3'>
              <label className='form-label'>Ürün Türü</label>
              <IPSelectBox
                isMulti
                isClearable
                placeholder='Tüm Türler (Cihaz / Tablet / …)'
                options={urunTuruSelectOptions}
                value={toSelectedOptions(urunTuru, urunTuruSelectOptions)}
                onChangeMulti={options => setUrunTuru(toValues(options))}
                isDisabled={loading}
              />
            </div>
            <div className='col-md-3'>
              <label className='form-label'>Ürün Hiyerarşisi</label>
              <IPSelectBox
                isMulti
                isClearable
                placeholder='Tüm Hiyerarşiler'
                options={hiyerarsiSelectOptions}
                value={toSelectedOptions(hiyerarsi, hiyerarsiSelectOptions)}
                onChangeMulti={options => setHiyerarsi(toValues(options))}
                isDisabled={loading}
              />
            </div>
            <div className='col-md-3'>
              <label className='form-label'>Satış Temsilcisi</label>
              <IPSelectBox
                isMulti
                isClearable
                placeholder='Tüm Temsilciler'
                options={temsilciSelectOptions}
                value={toSelectedOptions(satisTemsilcisi, temsilciSelectOptions)}
                onChangeMulti={options => setSatisTemsilcisi(toValues(options))}
                isDisabled={loading}
              />
            </div>
            <div className='col-md-3'>
              <label className='form-label'>Kanal Sorumlusu</label>
              <IPSelectBox
                isMulti
                isClearable
                placeholder='Tüm Kanal Sorumluları'
                options={kanalSelectOptions}
                value={toSelectedOptions(kanalSorumlusu, kanalSelectOptions)}
                onChangeMulti={options => setKanalSorumlusu(toValues(options))}
                isDisabled={loading}
              />
            </div>
            <div className='col-md-3'>
              <label className='form-label'>Bölge Sorumlusu</label>
              <IPSelectBox
                isMulti
                isClearable
                placeholder='Tüm Bölge Sorumluları'
                options={bolgeSelectOptions}
                value={toSelectedOptions(bolgeSorumlusu, bolgeSelectOptions)}
                onChangeMulti={options => setBolgeSorumlusu(toValues(options))}
                isDisabled={loading}
              />
            </div>
            <div className='col-md-3'>
              <label className='form-label'>Bayi Kodu (virgülle çoklu)</label>
              <IPInput
                type='text'
                value={bayiSap}
                placeholder='ör. 700002, 700074'
                onChange={e => setBayiSap(e.target.value)}
                // Her kod 10 haneye tamamlanır (başına 0).
                onBlur={e => setBayiSap(parseList(e.target.value).map(toDealerCode10).join(', '))}
              />
            </div>
            <div className='col-md-3'>
              <label className='form-label'>Ürün Kodu (virgülle çoklu)</label>
              <IPInput
                type='text'
                value={urunKodu}
                placeholder='Ürün Kodu'
                onChange={e => setUrunKodu(e.target.value)}
              />
            </div>
            <div className='col-md-3'>
              <label className='form-label'>Malzeme No (virgülle çoklu)</label>
              <IPInput
                type='text'
                value={malzemeNo}
                placeholder='Malzeme No'
                onChange={e => setMalzemeNo(e.target.value)}
              />
            </div>
            <div className='col-md-2'>
              <label className='form-label'>Tür</label>
              <IPSelectBox
                isClearable
                placeholder='Tüm Türler'
                options={ACTIVATION_TYPE_OPTIONS}
                value={toSelectedOption(activationType, ACTIVATION_TYPE_OPTIONS)}
                onChangeSingle={option => setActivationType((option ? String(option.value) : '') as ActivationType)}
                isDisabled={loading}
              />
            </div>

            <div className='col-md-2'>
              <label className='form-label'>Demo</label>
              <IPSelectBox
                isClearable
                placeholder='Tümü'
                options={DEMO_OPTIONS}
                value={toSelectedOption(demo, DEMO_OPTIONS)}
                onChangeSingle={option => setDemo((option ? String(option.value) : '') as DemoFilter)}
                isDisabled={loading}
              />
            </div>

            <div className='col-md-1 d-flex justify-content-end'>
              <IPButton text={loading ? 'Getiriliyor...' : 'Raporu Getir'} disabled={loading} onClick={getReport} />
            </div>
          </div>
        </div>

        {/* Raporun tarih ekseni — kutucuklar, tablo, sıralamalar, Excel ve grafikler bu seçime göre hesaplanır. */}
        <div style={{ ...styles.card, marginBottom: '1rem' }}>
          <div className='d-flex align-items-center flex-wrap gap-2'>
            <span style={styles.kpiLabel}>Rapor tarih ekseni</span>

            <div className='d-flex' role='group' aria-label='Rapor tarih ekseni'>
              {REPORT_AXIS_OPTIONS.map((option, index) => {
                const active = reportAxis === option.value
                const hovered = hoveredAxis === option.value

                return (
                  <button
                    key={option.value}
                    type='button'
                    aria-pressed={active}
                    onClick={() => setReportAxis(option.value)}
                    onMouseEnter={() => setHoveredAxis(option.value)}
                    onMouseLeave={() => setHoveredAxis(null)}
                    style={{
                      ...styles.axisButton,
                      backgroundColor: active ? (hovered ? '#1d4ed8' : COLORS.blue) : hovered ? '#e2e8f0' : '#ffffff',
                      color: active ? '#ffffff' : '#0f172a',
                      borderColor: active ? COLORS.blue : '#cbd5e1',
                      borderTopLeftRadius: index === 0 ? '6px' : 0,
                      borderBottomLeftRadius: index === 0 ? '6px' : 0,
                      borderTopRightRadius: index === REPORT_AXIS_OPTIONS.length - 1 ? '6px' : 0,
                      borderBottomRightRadius: index === REPORT_AXIS_OPTIONS.length - 1 ? '6px' : 0,
                      marginLeft: index === 0 ? 0 : '-1px',
                    }}
                  >
                    {option.label}
                  </button>
                )
              })}
            </div>
          </div>

          <div className='text-muted small mt-2 mb-0'>{reportAxisNote}</div>
        </div>

        {/* Özet kartlar */}
        <div className='row g-3 mb-3'>
          {kpiCards.map(card => (
            <div className='col-md-4' key={card.label}>
              <div style={styles.card}>
                <span style={styles.kpiLabel}>{card.label}</span>
                <p style={{ ...styles.kpiValue, color: card.color, ...(card.small ? { fontSize: '1.15rem' } : {}) }}>
                  {card.value}
                </p>
              </div>
            </div>
          ))}
        </div>

        {/* Grafikler */}
        <div className='d-flex justify-content-end mb-2'>
          <IPCheckbox
            id='stackedCharts'
            label='Grafikleri alt alta göster'
            checked={stackedCharts}
            onCheckedChange={setStackedCharts}
            removeMarginBottom
          />
        </div>

        <div className='row g-3 mb-3'>
          <div className={chartColumnClass}>
            <div style={styles.card}>
              <div style={styles.cardTitle}>Satış - Aktivasyon - Dönüşüm Trendi</div>
              <div style={{ height: chartHeight }}>
                {rows.length > 0 && trend.length > 0 ? (
                  <Line options={lineOptions} data={trendChartData} />
                ) : (
                  <div className='text-center text-muted py-5'>Grafik için veri bulunamadı.</div>
                )}
              </div>
            </div>
          </div>

          <div className={chartColumnClass}>
            <div style={styles.card}>
              <div style={styles.cardTitle}>Aktivasyon Tipleri Dağılımı</div>
              <div style={{ height: chartHeight }}>
                {rows.length > 0 && trend.length > 0 ? (
                  <Bar options={barOptions} data={activationChartData} />
                ) : (
                  <div className='text-center text-muted py-5'>Grafik için veri bulunamadı.</div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Sıralamalar */}
        <div className='row g-3 mb-3'>
          <div className='col-lg-4'>
            <div style={styles.card}>
              <div style={styles.cardTitle}>En Çok Satan Markalar</div>
              {brandStats.length === 0 && <div className='text-muted'>Veri bulunamadı.</div>}
              {brandStats.slice(0, 10).map((item, index) => (
                <div key={item.key} style={styles.listRow}>
                  <span>
                    {index + 1}. {item.label}
                  </span>
                  <b>{formatNumber(item.sales)}</b>
                </div>
              ))}
            </div>
          </div>

          <div className='col-lg-4'>
            <div style={styles.card}>
              <div style={styles.cardTitle}>En Başarılı Kotalar</div>
              {kotaStats.length === 0 && <div className='text-muted'>Veri bulunamadı.</div>}
              {kotaStats.slice(0, 10).map(item => (
                <div key={item.key} style={styles.listRow}>
                  <span>{item.label}</span>
                  <b>%{percentValue(item.activation, item.sales)}</b>
                </div>
              ))}
            </div>
          </div>

          <div className='col-lg-4'>
            <div style={styles.card}>
              <div style={styles.cardTitle}>En Başarılı Bayiler</div>
              {dealerStats.length === 0 && <div className='text-muted'>Veri bulunamadı.</div>}
              {dealerStats.slice(0, 10).map(item => (
                <div key={item.key} style={styles.listRow}>
                  <span>
                    {item.label} <small className='text-muted'>({normalizeDealerCode(item.key)})</small>
                  </span>
                  <b>{formatNumber(item.activation)}</b>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Sorumluluk kırılımları (aktivasyona göre) */}
        <div className='row g-3 mb-3'>
          <div className='col-lg-4'>
            <div style={styles.card}>
              <div style={styles.cardTitle}>En Başarılı Satış Temsilcileri</div>
              {salesRepStats.length === 0 && <div className='text-muted'>Veri bulunamadı.</div>}
              {salesRepStats.slice(0, 10).map((item, index) => (
                <div key={item.key} style={styles.listRow}>
                  <span>
                    {index + 1}. {item.label}
                  </span>
                  <b>{formatNumber(item.activation)}</b>
                </div>
              ))}
            </div>
          </div>

          <div className='col-lg-4'>
            <div style={styles.card}>
              <div style={styles.cardTitle}>En Başarılı Kanal Sorumluları</div>
              {channelStats.length === 0 && <div className='text-muted'>Veri bulunamadı.</div>}
              {channelStats.slice(0, 10).map((item, index) => (
                <div key={item.key} style={styles.listRow}>
                  <span>
                    {index + 1}. {item.label}
                  </span>
                  <b>{formatNumber(item.activation)}</b>
                </div>
              ))}
            </div>
          </div>

          <div className='col-lg-4'>
            <div style={styles.card}>
              <div style={styles.cardTitle}>En Başarılı Bölge Sorumluları</div>
              {regionStats.length === 0 && <div className='text-muted'>Veri bulunamadı.</div>}
              {regionStats.slice(0, 10).map((item, index) => (
                <div key={item.key} style={styles.listRow}>
                  <span>
                    {index + 1}. {item.label}
                  </span>
                  <b>{formatNumber(item.activation)}</b>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Detay tablo */}
        <div style={styles.card}>
          <div className='d-flex align-items-center justify-content-between mb-3'>
            <div style={{ ...styles.cardTitle, marginBottom: 0 }}>
              Detay Veri Tablosu{' '}
              {rows.length > 0 && <small className='text-muted'>({formatNumber(rows.length)} kayıt)</small>}
            </div>

            <div className='d-flex gap-2'>
              <IPButton
                text='Excel'
                disabled={rows.length === 0}
                onClick={onExportExcel}
                style={{ backgroundColor: '#16a34a' }}
              />
            </div>
          </div>

          {rows.length > 0 ? (
            <Table columns={columns} data={rows} />
          ) : (
            <div className='text-center text-muted py-4'>
              {searched ? 'Kayıt bulunamadı.' : 'Filtreleri seçip "Raporu Getir" butonuna basın.'}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default componentWithContext(VodafoneGeneralDashboard)
